import {
  useGetWorkingTimeBookingPairsForDate,
  useUpdateWorkingTimeBookingStatus,
  WorkingTimeBookingDto,
  WorkingTimeImportStatus,
} from "@/orval/gen"
import { CancelButton } from "@components/Buttons"
import { FormButtonGroup } from "@components/form-button-group"
import {
  ModalProps,
  Modal,
  Button,
  Alert,
  Group,
  Stack,
  Text,
  Checkbox,
  Flex,
} from "@mantine/core"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { getDateOnly } from "@components/date-only"
import { WorkingTimeBookingPairListItem } from "@/routes/app/-components/working-time-booking-card/WorkingTimeBookingPairListItem"

const requestStatuses: WorkingTimeImportStatus[] = [
  "Approved",
  "Rejected",
  "Requested",
]

interface Props extends ModalProps {
  request: WorkingTimeBookingDto | undefined
}

function WorkingTimeBookingRequestDetailsModalContent({
  request,
  onClose,
}: Props) {
  const [selectedBookings, setSelectedBookings] = React.useState<number[]>([])
  const { t } = useTranslation("app_server")
  const queryClient = useQueryClient()
  const { mutateAsync: updateStatusAsync } = useUpdateWorkingTimeBookingStatus({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ["pagedWorkingTimeBookings"],
        })
        onClose()
      },
      onError: showErrorNotification,
    },
  })
  const bookingsSelected = selectedBookings.length > 0
  const handleUpdateStatus = async (newStatus: WorkingTimeImportStatus) => {
    if (!request) return
    if (!bookingsSelected) return
    const ids = selectedBookings
    await updateStatusAsync({
      data: {
        workingTimeBookingIds: ids,
        newStatus: newStatus,
      },
    })
  }
  const { data } = useGetWorkingTimeBookingPairsForDate(
    { date: getDateOnly(request!.timeStamp), personId: request!.personId },
    { query: { enabled: !!request } }
  )
  React.useEffect(() => {
    if (!data || !request) return
    const initialPair = data.requested.find(
      (pair) =>
        (pair.checkIn &&
          pair.checkIn.workingTimeBookingId === request.workingTimeBookingId) ||
        (pair.checkOut &&
          pair.checkOut.workingTimeBookingId === request.workingTimeBookingId)
    )
    const initialPairSelectable =
      initialPair?.checkIn &&
      initialPair.checkOut &&
      (requestStatuses.includes(initialPair.checkIn.status) ||
        requestStatuses.includes(initialPair.checkOut.status))

    if (initialPairSelectable) {
      const initialSelectedIds = [
        initialPair.checkIn!.workingTimeBookingId,
        initialPair.checkOut!.workingTimeBookingId,
      ]
      setSelectedBookings(initialSelectedIds)
    }
  }, [data, request])

  const imported = data?.imported ?? []
  const requested = data?.requested ?? []

  if (!request)
    return (
      <Alert color="red" title={t("Error")}>
        {t("WorkingTimeBooking_NoBookingSelected")}
      </Alert>
    )

  return (
    <Stack>
      <Group align="flex-start">
        <Stack>
          <Text fw={500}>{t("UnimportedBookings")}</Text>
          {requested.length > 0 ? (
            requested.map((pair, index) => {
              const selectable =
                pair.checkIn &&
                pair.checkOut &&
                (requestStatuses.includes(pair.checkIn.status) ||
                  requestStatuses.includes(pair.checkOut.status))
              const isSelected =
                pair.checkIn &&
                selectedBookings.includes(pair.checkIn.workingTimeBookingId) &&
                pair.checkOut &&
                selectedBookings.includes(pair.checkOut.workingTimeBookingId)
              return (
                <Flex align={"center"} gap={"xs"} dir="row" key={index}>
                  <Checkbox
                    disabled={!selectable}
                    checked={!!isSelected}
                    onChange={(e) =>
                      e.target.checked
                        ? selectable &&
                          setSelectedBookings((prev) => [
                            ...prev,
                            pair.checkIn!.workingTimeBookingId,
                            pair.checkOut!.workingTimeBookingId,
                          ])
                        : setSelectedBookings((prev) =>
                            prev.filter(
                              (v) =>
                                v !== pair.checkIn!.workingTimeBookingId &&
                                v !== pair.checkOut!.workingTimeBookingId
                            )
                          )
                    }
                  ></Checkbox>
                  <WorkingTimeBookingPairListItem bookingPair={pair} />
                </Flex>
              )
            })
          ) : (
            <Text fz={"sm"} fw={"lighter"}>
              {t("NoUnimportedBookings")}
            </Text>
          )}
        </Stack>
        <Stack>
          <Text fw={500}>{t("ImportedBookings")}</Text>
          {imported.length > 0 ? (
            imported.map((pair, index) => (
              <WorkingTimeBookingPairListItem key={index} bookingPair={pair} />
            ))
          ) : (
            <Text fz={"sm"} fw={"lighter"}>
              {t("NoImportedBookings")}
            </Text>
          )}
        </Stack>
      </Group>
      <FormButtonGroup>
        <CancelButton onClick={onClose} />
        <Button
          color="red"
          disabled={!bookingsSelected}
          onClick={async () => await handleUpdateStatus("Rejected")}
        >
          {t("Reject")}
        </Button>
        <Button
          disabled={!bookingsSelected}
          onClick={async () => await handleUpdateStatus("Approved")}
        >
          {t("Approve")}
        </Button>
      </FormButtonGroup>
    </Stack>
  )
}

export function WorkingTimeBookingRequestDetailsModal(props: Props) {
  const { t } = useTranslation("app_server")
  return (
    <Modal size="auto" {...props} title={t("WorkingTimeBookingDetails")}>
      {props.opened && (
        <WorkingTimeBookingRequestDetailsModalContent {...props} />
      )}
    </Modal>
  )
}
